import { FormEvent, ReactNode, useMemo, useState } from "react";

export function RestaurantDrawer({ open, eyebrow, title, subtitle, children, footer, onClose, wide = false }: {
  open: boolean;
  eyebrow?: string;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  wide?: boolean;
}) {
  if (!open) return null;
  return <div className="rdv4-overlay" role="presentation" onMouseDown={(event) => {
    if (event.target === event.currentTarget) onClose();
  }}>
    <aside className={`rdv4-drawer${wide ? " rdv4-drawer-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
      <header className="rdv4-drawer-header">
        <div>{eyebrow ? <span className="rdv4-eyebrow">{eyebrow}</span> : null}<h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div>
        <button className="rdv4-icon-button" type="button" aria-label="Cerrar" onClick={onClose}>×</button>
      </header>
      <div className="rdv4-drawer-body">{children}</div>
      {footer ? <footer className="rdv4-drawer-footer">{footer}</footer> : null}
    </aside>
  </div>;
}

export function ConfirmDialog({ open, title, message, confirmLabel = "Eliminar", danger = true, tone, onCancel, onConfirm }: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  danger?: boolean;
  tone?: "danger" | "neutral";
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const isDanger = tone ? tone === "danger" : danger;
  return <div className="rdv4-overlay rdv4-overlay-center" role="presentation">
    <section className="rdv4-confirm" role="alertdialog" aria-modal="true" aria-label={title}>
      <div className={`rdv4-confirm-icon${isDanger ? " danger" : ""}`}>{isDanger ? "!" : "?"}</div>
      <h3>{title}</h3>
      <p>{message}</p>
      <div className="rdv4-confirm-actions">
        <button type="button" className="rdv4-button-secondary" onClick={onCancel}>Cancelar</button>
        <button type="button" className={isDanger ? "rdv4-button-danger" : "rdv4-button-primary"} onClick={onConfirm}>{confirmLabel}</button>
      </div>
    </section>
  </div>;
}

export function CreatableCombobox({ label, value, options, placeholder, onChange, onCreate, required = false }: {
  label: string;
  value: string;
  options: readonly string[];
  placeholder?: string;
  onChange: (value: string) => void;
  onCreate?: (value: string) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const clean = value.trim();
  const filtered = useMemo(() => {
    const needle = clean.toLocaleLowerCase("es");
    return [...options]
      .filter((option) => !needle || option.toLocaleLowerCase("es").includes(needle))
      .sort((a, b) => {
        const aStart = a.toLocaleLowerCase("es").startsWith(needle) ? 0 : 1;
        const bStart = b.toLocaleLowerCase("es").startsWith(needle) ? 0 : 1;
        return aStart - bStart || a.localeCompare(b, "es");
      });
  }, [clean, options]);
  const exact = options.some((option) => option.toLocaleLowerCase("es") === clean.toLocaleLowerCase("es"));
  return <label className="rdv4-field rdv4-combobox-field">
    <span>{label}</span>
    <div className="rdv4-combobox">
      <input
        value={value}
        placeholder={placeholder}
        required={required}
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={(event) => { onChange(event.target.value); setOpen(true); }}
        onKeyDown={(event) => {
          if (event.key === "Escape") setOpen(false);
          if (event.key === "Enter" && open && clean && !exact && onCreate) {
            event.preventDefault();
            onCreate(clean);
            onChange(clean);
            setOpen(false);
          }
        }}
      />
      <button type="button" tabIndex={-1} aria-label={`Mostrar opciones de ${label}`} onClick={() => setOpen((current) => !current)}>⌄</button>
      {open ? <div className="rdv4-combobox-menu">
        {filtered.slice(0, 8).map((option) => <button type="button" key={option} onMouseDown={(event) => event.preventDefault()} onClick={() => { onChange(option); setOpen(false); }}>{option}</button>)}
        {clean && !exact && onCreate ? <button type="button" className="create" onMouseDown={(event) => event.preventDefault()} onClick={() => { onCreate(clean); onChange(clean); setOpen(false); }}>+ Crear “{clean}”</button> : null}
        {filtered.length === 0 && (!clean || !onCreate) ? <span className="rdv4-combobox-empty">Sin coincidencias</span> : null}
      </div> : null}
    </div>
  </label>;
}

export function SearchInput({ value, onChange, placeholder = "Buscar…", ariaLabel = "Buscar" }: {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  ariaLabel?: string;
}) {
  return <label className="rdv4-search"><span aria-hidden="true">⌕</span><input aria-label={ariaLabel} placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} />{value ? <button type="button" aria-label="Limpiar búsqueda" onClick={() => onChange("")}>×</button> : null}</label>;
}

export function FilterSelect({ label, value, onChange, options }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
}) {
  return <label className="rdv4-filter-select"><span>{label}</span><select value={value} onChange={(event) => onChange(event.target.value)}>{options.map((option) => <option value={option.value} key={option.value}>{option.label}</option>)}</select></label>;
}

export function SegmentedControl({ value, onChange, options, ariaLabel = "Opciones de vista" }: {
  value: string;
  onChange: (value: string) => void;
  options: readonly { value: string; label: string }[];
  ariaLabel?: string;
}) {
  return <div className="rdv4-segmented" role="group" aria-label={ariaLabel}>{options.map((option) => <button type="button" key={option.value} className={value === option.value ? "active" : ""} onClick={() => onChange(option.value)}>{option.label}</button>)}</div>;
}

export function StatusBadge({ tone, children }: { tone: "success" | "warning" | "danger" | "info" | "neutral"; children: ReactNode }) {
  return <span className={`rdv4-badge rdv4-badge-${tone}`}>{children}</span>;
}

export function EmptyState({ title, description, action }: { title: string; description: string; action?: ReactNode }) {
  return <div className="rdv4-empty"><div className="rdv4-empty-icon">◇</div><strong>{title}</strong><p>{description}</p>{action}</div>;
}

export function Toast({ message, tone = "success", onClose }: { message: string | null; tone?: "success" | "danger"; onClose: () => void }) {
  if (!message) return null;
  return <div className={`rdv4-toast rdv4-toast-${tone}`} role="status"><span>{tone === "success" ? "✓" : "!"}</span><strong>{message}</strong><button type="button" aria-label="Cerrar notificación" onClick={onClose}>×</button></div>;
}

export function FormActions({ children }: { children: ReactNode }) {
  return <div className="rdv4-form-actions">{children}</div>;
}

export function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return <label className="rdv4-field"><span>{label}</span>{children}{hint ? <small>{hint}</small> : null}</label>;
}

export function DrawerForm({ onSubmit, children }: { onSubmit: (event: FormEvent<HTMLFormElement>) => void; children: ReactNode }) {
  return <form className="rdv4-drawer-form" onSubmit={onSubmit}>{children}</form>;
}
